import { z } from "zod";
import type { Operation } from "../../types";
import { crmIdParams } from "./_shared";

const statusRow = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  sort_key: z.string(),
  is_terminal: z.number(),
});

const sourceRow = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
});

const statusesList: Operation<
  void,
  void,
  void,
  { statuses: z.infer<typeof statusRow>[] }
> = {
  method: "GET",
  path: () => "/api/crm/settings/statuses",
  response: z.object({ statuses: z.array(statusRow) }),
};

const statusCreate: Operation<
  void,
  void,
  { slug: string; name: string; is_terminal?: boolean; color?: string | null },
  z.infer<typeof statusRow>
> = {
  method: "POST",
  path: () => "/api/crm/settings/statuses",
  body: z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    is_terminal: z.boolean().optional(),
    color: z.string().nullable().optional(),
  }),
  response: statusRow,
  invalidates: [statusesList],
};

const sourcesList: Operation<
  void,
  void,
  void,
  { sources: z.infer<typeof sourceRow>[] }
> = {
  method: "GET",
  path: () => "/api/crm/settings/sources",
  response: z.object({ sources: z.array(sourceRow) }),
};

const sourceCreate: Operation<
  void,
  void,
  { slug: string; name: string },
  z.infer<typeof sourceRow>
> = {
  method: "POST",
  path: () => "/api/crm/settings/sources",
  body: z.object({ slug: z.string().min(1), name: z.string().min(1) }),
  response: sourceRow,
  invalidates: [sourcesList],
};

const statusUpdate: Operation<
  { id: number },
  void,
  { name?: string; is_terminal?: boolean; color?: string | null },
  { ok: true }
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/settings/statuses/${id}`,
  params: crmIdParams,
  body: z.object({
    name: z.string().min(1).optional(),
    is_terminal: z.boolean().optional(),
    color: z.string().nullable().optional(),
  }),
  response: z.object({ ok: z.literal(true) }),
  invalidates: [statusesList],
};

const statusArchive: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "DELETE",
  path: ({ id }) => `/api/crm/settings/statuses/${id}`,
  params: crmIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [statusesList],
};

const sourceUpdate: Operation<
  { id: number },
  void,
  { name?: string },
  { ok: true }
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/settings/sources/${id}`,
  params: crmIdParams,
  body: z.object({ name: z.string().min(1).optional() }),
  response: z.object({ ok: z.literal(true) }),
  invalidates: [sourcesList],
};

const sourceArchive: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "DELETE",
  path: ({ id }) => `/api/crm/settings/sources/${id}`,
  params: crmIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [sourcesList],
};

// Email transport config (the log→send switch). The API key is NEVER part of
// this contract — it lives in the CRM_EMAIL_API_KEY env var, never the DB.
const emailConfigBody = z.object({
  provider: z.enum(["log", "resend", "http"]).optional(),
  from: z.string().optional(),
  endpoint: z.string().optional(),
  apiKeyHeader: z.string().optional(),
  apiKeyFormat: z.string().optional(),
});

const emailGet: Operation<
  void,
  void,
  void,
  { config: z.infer<typeof emailConfigBody>; configured: boolean; keyFromEnv: boolean }
> = {
  method: "GET",
  path: () => "/api/crm/settings/email",
  response: z.object({
    config: emailConfigBody,
    configured: z.boolean(),
    keyFromEnv: z.boolean(),
  }),
};

const emailSet: Operation<void, void, z.infer<typeof emailConfigBody>, { ok: true }> = {
  method: "PUT",
  path: () => "/api/crm/settings/email",
  body: emailConfigBody,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [emailGet],
};

// SMS transport config — same shape as email; the auth token lives in
// CRM_SMS_AUTH_TOKEN / CRM_SMS_API_KEY, never the DB.
const smsConfigBody = z.object({
  provider: z.enum(["log", "twilio", "http"]).optional(),
  from: z.string().optional(),
  accountSid: z.string().optional(),
  endpoint: z.string().optional(),
  apiKeyHeader: z.string().optional(),
  apiKeyFormat: z.string().optional(),
});

const smsGet: Operation<
  void,
  void,
  void,
  { config: z.infer<typeof smsConfigBody>; configured: boolean; keyFromEnv: boolean }
> = {
  method: "GET",
  path: () => "/api/crm/settings/sms",
  response: z.object({
    config: smsConfigBody,
    configured: z.boolean(),
    keyFromEnv: z.boolean(),
  }),
};

const smsSet: Operation<void, void, z.infer<typeof smsConfigBody>, { ok: true }> = {
  method: "PUT",
  path: () => "/api/crm/settings/sms",
  body: smsConfigBody,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [smsGet],
};

// Quarter bookings target — the forecast's "are we going to hit the number?"
// quota (minor units, or null when unset). Drives /customers?view=forecast.
const forecastTargetGet: Operation<void, void, void, { minor: number | null }> = {
  method: "GET",
  path: () => "/api/crm/settings/forecast-target",
  response: z.object({ minor: z.number().int().nonnegative().nullable() }),
};

const forecastTargetSet: Operation<
  void,
  void,
  { minor: number | null },
  { ok: true }
> = {
  method: "PUT",
  path: () => "/api/crm/settings/forecast-target",
  body: z.object({ minor: z.number().int().nonnegative().nullable() }),
  response: z.object({ ok: z.literal(true) }),
  invalidates: [forecastTargetGet],
};

export const settings = {
  statuses_list: statusesList,
  status_create: statusCreate,
  status_update: statusUpdate,
  status_archive: statusArchive,
  sources_list: sourcesList,
  source_create: sourceCreate,
  source_update: sourceUpdate,
  source_archive: sourceArchive,
  email_get: emailGet,
  email_set: emailSet,
  sms_get: smsGet,
  sms_set: smsSet,
  forecast_target_get: forecastTargetGet,
  forecast_target_set: forecastTargetSet,
};
